<script lang="ts" setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router/composables'
import type { HomePageConfig } from '../configs/types'
import ModuleBanner from './modules/ModuleBanner.vue'
import ModuleGrid2x4 from './modules/ModuleGrid2x4.vue'
import ModuleFeatured1Plus4 from './modules/ModuleFeatured1Plus4.vue'
import ModuleGrid1x4 from './modules/ModuleGrid1x4.vue'
import ModuleFeed from './modules/ModuleFeed.vue'
import ModuleTagsWithBg from './modules/ModuleTagsWithBg.vue'
import ModuleRankingBig from './modules/ModuleRankingBig.vue'
import ModuleBookSwiper from './modules/ModuleBookSwiper.vue'
import { COLUMN_TYPE } from '@/constants'

const props = defineProps<{
  config: HomePageConfig
  setInfiniteRef: (el: HTMLElement | null) => void
  setInputRef: (el: any) => void
  input: string
  inputFocused: boolean
  pageList: any[]
  pageInfo: any
  skeleton: boolean
  modules: any[]
  currentTagId: string
  feeds: any[]
  feedShowName: string
  feedId: string
  feedsColumnType: string | number
  feedFetching: boolean
  feedEnding: boolean
  homeBgUrl: any
  switchNavTrack: (item: any) => void
  onSearch: () => void
  gotoCollection: (id: string) => void
  gotoTags: (id: string) => void
  gotoBook: (id: string, firstChapterId?: string) => void
  getCurrentTagResources: (col: any) => any[]
  getCurrentShowTags: (col: any) => any[]
  handleTagClick: (tagId: string) => void
}>()

// 定义 emit 以保持与 default 组件的类型兼容性
defineEmits<{
  (e: 'update:input', value: string): void
}>()

const router = useRouter()

const localHomeBgUrl = ref(props.homeBgUrl)
watch(() => props.homeBgUrl, (newVal) => {
  localHomeBgUrl.value = newVal
}, { immediate: true })

// 更新背景图
function handleUpdateBgUrl(url: string) {
  localHomeBgUrl.value = url
}

// 定义样式变量供 CSS v-bind 使用
const homeBgUrlComputed = computed(() => `url(${localHomeBgUrl.value})`)
const tabActiveColor = computed(() => props.config.theme.tabActiveColor)
const stickyHeaderBg = computed(() => props.config.theme.stickyHeaderBg)

// 顶部 Banner 模块：仅当首项为 BANNER(9) 时生效
const bannerModule = computed(() => {
  const first = props.modules[0]
  return first?.columnType === COLUMN_TYPE.BANNER ? first : null
})

// 其他模块：剔除被顶部接管的首项
const otherModules = computed(() => {
  return bannerModule.value ? props.modules.slice(1) : props.modules
})

// 是否有顶部 Banner 模块
const hasBanner = computed(() => !!bannerModule.value)

// 滚动状态
const scrollTop = ref(0)
const scrollContainer = ref<HTMLElement | null>(null)

// 是否显示 sticky-header 背景
// 有 Banner 时：滚动超过 10px 才显示背景
// 无 Banner 时：始终显示背景
const showHeaderBg = computed(() => {
  if (!hasBanner.value)
    return true
  return scrollTop.value > 10
})

// 监听滚动
function handleScroll(e: Event) {
  const target = e.target as HTMLElement
  scrollTop.value = target.scrollTop
}

// 设置滚动容器引用
function setScrollRef(el: HTMLElement | null) {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)

  scrollContainer.value = el
  if (el)
    el.addEventListener('scroll', handleScroll, { passive: true })

  props.setInfiniteRef(el)
}

onUnmounted(() => {
  if (scrollContainer.value)
    scrollContainer.value.removeEventListener('scroll', handleScroll)
})

// 跳转到福利页面
function gotoWelfare() {
  router.push('/welfare')
}
</script>

<template>
  <div :ref="setScrollRef" class="h-100dvh overflow-y-scroll">
    <!-- 固定头部：搜索 + Tab -->
    <!-- light-mode: 无 Banner 或滚动时显示背景，使用深色文字 -->
    <div class="sticky-header pt-14px" :class="{ 'show-bg': showHeaderBg, 'light-mode': !hasBanner || showHeaderBg }">
      <!-- Search Bar + Welfare Icon -->
      <div class="search-row flex items-center px-15px space-x-12px">
        <div class="search-bar flex-1 h-32px flex items-center px-12px cursor-pointer" @click="onSearch">
          <!-- 有 Banner 且未滚动时用白色图标，否则用灰色图标 -->
          <img v-if="hasBanner && !showHeaderBg" src="/images/novelarc/icon-search-white.png" width="16px" height="16px" class="shrink-0">
          <img v-else src="/images/novelarc/search-icon.png" width="16px" height="16px" class="shrink-0">
          <span class="ml-8px text-12px search-text">{{ config.texts.searchPlaceholder }}</span>
        </div>
        <img src="/images/novelarc/welfare-box.png" width="26px" height="26px" class="cursor-pointer shrink-0" @click="gotoWelfare">
      </div>

      <!-- Tab -->
      <div class="px-15px overflow-x-auto">
        <div class="flex items-center space-x-24px">
          <div v-for="item in pageList" :key="item.id">
            <div class="home-tab relative" :class="{ active: item.id === pageInfo.id }" @click="switchNavTrack(item)">
              <span>{{ item.showName }}</span>
              <i v-if="item.id === pageInfo.id" class="tab-underline" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="home-bg pb-20" :class="{ 'pt-[10px]': !hasBanner }">
      <!-- Modules -->
      <div v-if="skeleton" class="h-100dvh">
        <div class="pt-20vh flex justify-center">
          <BasicSpinner class="mx-auto" />
        </div>
        <p class="mt-4 text-center font-bold">
          {{ config.texts.loading }}
        </p>
      </div>
      <div v-else class="px-0">
        <!-- 顶部 Banner 模块：撑满屏宽，向上延伸到 sticky-header 下方 -->
        <div v-if="bannerModule" class="banner-wrapper">
          <ModuleBanner
            :module="bannerModule"
            :config="config"
            position="top"
            :goto-book="gotoBook"
            @update:bg-url="handleUpdateBgUrl"
          />
        </div>

        <!-- 其他模块 -->
        <div v-for="(col, index) in otherModules" :key="col.id" :class="{ 'mt-24px': index > 0 || bannerModule }">
          <!-- 非顶部 Banner -->
          <template v-if="col.columnType === COLUMN_TYPE.BANNER">
            <div class="px-15px">
              <ModuleBanner
                :module="col"
                :config="config"
                position="inline"
                :goto-book="gotoBook"
              />
            </div>
          </template>
          <!-- 轮播作品（SLIDE_BOOK 改用 BookSwiper 与 SWIPER 对齐） -->
          <template v-else-if="col.columnType === COLUMN_TYPE.SLIDE_BOOK">
            <ModuleBookSwiper :module="col" :config="config" card-class="p-0 px-15px" class="my-4 w-full" />
          </template>
          <!-- 2行3列模块 -->
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_3">
            <ModuleTagsWithBg
              card-class="p-0"
              :module="col"
              :config="config"
              :current-tag-id="currentTagId"
              :goto-book="gotoBook"
              :goto-tags="gotoTags"
              :get-current-tag-resources="getCurrentTagResources"
              :get-current-show-tags="getCurrentShowTags"
              :handle-tag-click="handleTagClick"
            />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_PLUS_COL_4">
            <ModuleFeatured1Plus4
              card-class="p-0 px-15px"
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_4">
            <ModuleGrid2x4
              card-class="p-0 px-15px"
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_MUL_COL_4">
            <ModuleGrid1x4
              card-class="p-0 px-15px"
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
            />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_3_MUL_COL_4">
            <ModuleRankingBig :module="col" :config="config" card-class="p-0 px-15px" class="my-4 w-full" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.SWIPER">
            <ModuleBookSwiper :module="col" :config="config" card-class="p-0 px-15px" class="my-4 w-full" />
          </template>
        </div>
      </div>

      <!-- Feed -->
      <ModuleFeed
        v-if="feeds.length"
        card-class="p-0 px-15px mt-6"
        :feeds="feeds"
        :feed-show-name="feedShowName"
        :feed-id="feedId"
        :feeds-column-type="feedsColumnType"
        :feed-fetching="feedFetching"
        :feed-ending="feedEnding"
        :config="config"
        :goto-book="gotoBook"
      />
    </div>

    <BasicTabbar />
  </div>
</template>

<style lang="scss" scoped>
/* 固定头部：搜索 + Tab */
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;

  /* 背景层用伪元素实现 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100px;
    background: v-bind(stickyHeaderBg);
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
    pointer-events: none;
  }

  /* 显示背景时 */
  &.show-bg::before {
    opacity: 1;
  }
}

/* Banner 容器：撑满屏宽，向上偏移让 banner 延伸到 sticky-header 下方 */
/* 多覆盖 1px 以消除滚动时顶部亚像素缝隙透出底部图案 */
.banner-wrapper {
  position: relative;
  margin-top: -101px;
  z-index: 0;
}

.home-bg {
  position: relative;
  z-index: 1;
}

/* 有 Banner 时的搜索框样式 */
.search-bar {
  background: rgba(255, 255, 255, 0.1);
  border-radius: 16px;
}

.search-text {
  color: rgba(255, 255, 255, 0.5);
}

/* light-mode 时的搜索框样式（无 Banner 或滚动时） */
.sticky-header.light-mode .search-bar {
  background: rgba(0, 0, 0, 0.05);
}

.sticky-header.light-mode .search-text {
  color: #969CAA;
}

/* 有 Banner 时的 Tab 样式 */
.home-tab {
  cursor: pointer;
  padding: 10px 0;

  span {
    font-weight: 500;
    font-size: 14px;
    color: #FFFFFF;
    opacity: 0.7;
    line-height: 16px;
    white-space: nowrap;
  }
}

/* light-mode 时的 Tab 样式（无 Banner 或滚动时） */
.sticky-header.light-mode .home-tab span {
  color: #32353A;
  opacity: 1;
}

.home-tab.active {
  span {
    font-size: 16px;
    background: v-bind(tabActiveColor);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    opacity: 1;
    font-weight: bold;
  }

  .tab-underline {
    display: block;
    position: absolute;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
    width: 18px;
    height: 2px;
    background: v-bind(tabActiveColor);
    border-radius: 2px;
  }
}
</style>
